import React, { useState, useEffect, useRef } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActorConfigForm } from "./ActorConfigForm";
import { ActorRunsTable } from "./ActorRunsTable";
import { LogViewer } from "@/pages/ScraperStatus";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Loader2,
  RefreshCw,
  ChevronDown,
  Plus,
  X,
  ArrowLeft,
  Download,
  Info,
} from "lucide-react";
import { Doc, Id } from "../../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";

interface ActorTabsProps {
  selectedSource: any;
  logs: any[];
  selectedRunId?: string | null;
  onRunSelect?: (runId: string | null) => void;
}

export function ActorTabs({
  selectedSource,
  logs,
  selectedRunId,
  onRunSelect,
}: ActorTabsProps) {
  const { toast } = useToast();
  const getActorSettings = useAction(api.apify.getActorSettings);
  const getActorInputSchema = useAction(api.apify.getActorInputSchema);
  const getRunDataset = useAction(api.apify.getRunDataset);
  const updateSource = useMutation(api.sources.update);
  const channels = useQuery(api.channels.list) || [];

  // Get runs from database instead of Apify API
  const databaseRuns = useQuery(
    api.runs.listBySourceId,
    selectedSource?._id
      ? { sourceId: selectedSource._id as Id<"scraperSources">, limit: 50 }
      : "skip"
  ) as Doc<"runs">[] | undefined;

  // Get logs for selected run
  const runLogs = useQuery(
    api.logs.listByRunId,
    selectedRunId ? { runId: selectedRunId } : "skip"
  ) as any[] | undefined;

  const [actorSettings, setActorSettings] = useState<any>(null);
  const [inputSchema, setInputSchema] = useState<any>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isRefreshingSchema, setIsRefreshingSchema] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingFilters, setIsSavingFilters] = useState(false);
  const [datasetItems, setDatasetItems] = useState<any[]>([]);
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const datasetPreviewRef = useRef<HTMLDivElement>(null);

  // Filter configuration state
  const [minOte, setMinOte] = useState<number>(60000);
  const [minHourly, setMinHourly] = useState<number>(15);
  const [requireRemote, setRequireRemote] = useState<boolean>(true);
  const [requireContract, setRequireContract] = useState<boolean>(true);
  const [requireTargetTitle, setRequireTargetTitle] = useState<boolean>(true);
  const [excludeCommissionOnly, setExcludeCommissionOnly] =
    useState<boolean>(true);
  const [excludeTitleList, setExcludeTitleList] = useState<boolean>(true);
  const [excludeHybridRemote, setExcludeHybridRemote] = useState<boolean>(true);
  const [excludeFullTime, setExcludeFullTime] = useState<boolean>(true);
  const [excludedTitles, setExcludedTitles] = useState<string[]>([]);
  const [newTitleInput, setNewTitleInput] = useState<string>("");
  const [isTitlesListOpen, setIsTitlesListOpen] = useState<boolean>(false);
  const [telegramChannelId, setTelegramChannelId] = useState<string>("");
  const [telegramMessageTemplate, setTelegramMessageTemplate] =
    useState<string>("");
  const [isSavingTelegram, setIsSavingTelegram] = useState(false);
  const [isTemplateVarsOpen, setIsTemplateVarsOpen] = useState(false);

  const actorId = selectedSource?.config?.actorId;
  const savedSchema = selectedSource?.config?.inputSchema;
  const filterConfig = selectedSource?.config?.filterConfig;

  useEffect(() => {
    if (actorId) {
      loadActorData();
    }
  }, [actorId]);

  // Initialize filter config from source
  useEffect(() => {
    if (filterConfig) {
      if (filterConfig.min_ote !== undefined) {
        setMinOte(filterConfig.min_ote);
      }
      if (filterConfig.min_hourly !== undefined) {
        setMinHourly(filterConfig.min_hourly);
      }
      if (filterConfig.require_remote !== undefined) {
        setRequireRemote(filterConfig.require_remote);
      }
      if (filterConfig.require_contract !== undefined) {
        setRequireContract(filterConfig.require_contract);
      }
      if (filterConfig.require_target_title !== undefined) {
        setRequireTargetTitle(filterConfig.require_target_title);
      }
      if (filterConfig.exclude_commission_only !== undefined) {
        setExcludeCommissionOnly(filterConfig.exclude_commission_only);
      }
      if (filterConfig.exclude_title_list !== undefined) {
        setExcludeTitleList(filterConfig.exclude_title_list);
      }
      if (filterConfig.exclude_hybrid_remote !== undefined) {
        setExcludeHybridRemote(filterConfig.exclude_hybrid_remote);
      }
      if (filterConfig.exclude_full_time !== undefined) {
        setExcludeFullTime(filterConfig.exclude_full_time);
      }
      if (filterConfig.excluded_titles !== undefined) {
        console.log(
          "[ActorTabs] Loading excluded_titles from DB:",
          filterConfig.excluded_titles
        );
        setExcludedTitles(filterConfig.excluded_titles);
      } else {
        // Start with empty list if not set
        console.log(
          "[ActorTabs] No excluded_titles in DB, initializing empty array"
        );
        setExcludedTitles([]);
      }
    } else {
      // Start with empty list if no filter config exists
      console.log(
        "[ActorTabs] No filterConfig in source, initializing empty array"
      );
      setExcludedTitles([]);
    }
  }, [filterConfig]);

  // Initialize Telegram channel and template from source
  useEffect(() => {
    if (selectedSource?.telegramChannelId) {
      setTelegramChannelId(selectedSource.telegramChannelId);
    } else {
      setTelegramChannelId("");
    }
    if (selectedSource?.telegramMessageTemplate) {
      setTelegramMessageTemplate(selectedSource.telegramMessageTemplate);
    } else {
      // Default template
      setTelegramMessageTemplate(`💼 *{{title}}*
🏢 {{company}}
💰 {{type}}: {{rate}}
📍 {{location}}
{{#if url}}🔗 [View Job]({{url}}){{/if}}`);
    }
  }, [
    selectedSource?.telegramChannelId,
    selectedSource?.telegramMessageTemplate,
  ]);

  // Load dataset when a run is selected
  useEffect(() => {
    if (selectedRunId) {
      const loadDataset = async () => {
        setIsLoadingDataset(true);
        try {
          const result = await getRunDataset({ runId: selectedRunId });
          setDatasetItems(result?.items || []);
          if (result?.error) {
            toast({
              title: "Warning",
              description: result.error,
              variant: "destructive",
            });
          }
        } catch (error: any) {
          toast({
            title: "Failed to load dataset",
            description: error.message,
            variant: "destructive",
          });
        } finally {
          setIsLoadingDataset(false);
        }
      };
      loadDataset();
    } else {
      setDatasetItems([]);
    }
  }, [selectedRunId, getRunDataset, toast]);

  const loadActorData = async () => {
    if (!actorId) return;

    setIsLoadingSettings(true);

    try {
      // Use saved schema if available and valid, otherwise fetch from Apify
      const hasValidSavedSchema =
        savedSchema &&
        typeof savedSchema === "object" &&
        savedSchema.properties &&
        Object.keys(savedSchema.properties).length > 0;

      const schemaPromise = hasValidSavedSchema
        ? Promise.resolve(savedSchema)
        : getActorInputSchema({ actorId });

      const [settings, schema] = await Promise.all([
        getActorSettings({ actorId }),
        schemaPromise,
      ]);

      setActorSettings(settings);
      setInputSchema(schema);

      // If we fetched a new schema and it's valid, save it to the source
      if (
        !hasValidSavedSchema &&
        schema &&
        schema.properties &&
        Object.keys(schema.properties).length > 0
      ) {
        try {
          await updateSource({
            id: selectedSource._id,
            config: {
              ...selectedSource.config,
              inputSchema: schema,
            },
          });
        } catch (saveError) {
          // Non-critical - schema will still be displayed, just not saved
          console.warn("Failed to save fetched schema:", saveError);
        }
      }
    } catch (error: any) {
      toast({
        title: "Failed to load actor data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleRefreshSchema = async () => {
    if (!actorId) return;

    setIsRefreshingSchema(true);
    try {
      const schema = await getActorInputSchema({ actorId });
      setInputSchema(schema);

      // Save the refreshed schema to the source
      await updateSource({
        id: selectedSource._id,
        config: {
          ...selectedSource.config,
          inputSchema: schema,
        },
      });

      toast({
        title: "Schema refreshed",
        description: "Input schema has been updated from Apify",
      });
    } catch (error: any) {
      toast({
        title: "Failed to refresh schema",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsRefreshingSchema(false);
    }
  };

  const handleSaveConfiguration = async (values: Record<string, any>) => {
    setIsSaving(true);
    try {
      await updateSource({
        id: selectedSource._id,
        config: {
          ...selectedSource.config,
          input: values,
        },
      });

      toast({
        title: "Configuration saved",
        description: "Actor input configuration has been updated",
      });
    } catch (error: any) {
      toast({
        title: "Failed to save configuration",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveFilterConfig = async () => {
    setIsSavingFilters(true);
    try {
      const filterConfigToSave = {
        min_ote: minOte,
        min_hourly: minHourly,
        require_remote: requireRemote,
        require_contract: requireContract,
        require_target_title: requireTargetTitle,
        exclude_commission_only: excludeCommissionOnly,
        exclude_title_list: excludeTitleList,
        exclude_hybrid_remote: excludeHybridRemote,
        exclude_full_time: excludeFullTime,
        excluded_titles: excludedTitles, // Always include, even if empty array
      };

      // Ensure we preserve all existing config fields (actorId, actorName, input, inputSchema, etc.)
      const configToSave = {
        ...(selectedSource.config || {}),
        filterConfig: filterConfigToSave,
      };

      console.log("[ActorTabs] Saving filter config:", {
        sourceId: selectedSource._id,
        excluded_titles: excludedTitles,
        excluded_titles_count: excludedTitles.length,
        full_filterConfig: filterConfigToSave,
        configToSave: configToSave,
        hasFilterConfig: !!configToSave.filterConfig,
      });

      const result = await updateSource({
        id: selectedSource._id,
        config: configToSave,
      });

      console.log("[ActorTabs] Save result:", result);

      toast({
        title: "Filter configuration saved",
        description: `Job filtering settings have been updated${excludedTitles.length > 0 ? ` (${excludedTitles.length} excluded title${excludedTitles.length !== 1 ? "s" : ""})` : ""}`,
      });
    } catch (error: any) {
      console.error("[ActorTabs] Failed to save filter config:", error);
      toast({
        title: "Failed to save filter configuration",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingFilters(false);
    }
  };

  const handleAddTitle = () => {
    const trimmed = newTitleInput.trim().toLowerCase();
    if (trimmed && !excludedTitles.includes(trimmed)) {
      setExcludedTitles([...excludedTitles, trimmed]);
      setNewTitleInput("");
    }
  };

  const handleRemoveTitle = (title: string) => {
    setExcludedTitles(excludedTitles.filter((t) => t !== title));
  };

  const handleSaveTelegramChannel = async () => {
    setIsSavingTelegram(true);
    try {
      await updateSource({
        id: selectedSource._id,
        telegramChannelId: telegramChannelId.trim() || undefined,
        telegramMessageTemplate: telegramMessageTemplate.trim() || undefined,
      });

      toast({
        title: "Telegram configuration saved",
        description: telegramChannelId.trim()
          ? `Jobs will be sent to ${telegramChannelId.trim()} after successful runs`
          : "Telegram notifications disabled for this source",
      });
    } catch (error: any) {
      console.error(
        "[ActorTabs] Failed to save Telegram configuration:",
        error
      );
      toast({
        title: "Failed to save Telegram configuration",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingTelegram(false);
    }
  };

  const handleViewDataset = async (runId: string) => {
    setIsLoadingDataset(true);

    // Scroll immediately to dataset preview section (it will show loading state)
    setTimeout(() => {
      datasetPreviewRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 50);

    try {
      const result = await getRunDataset({ runId });
      // getRunDataset returns { items: [], error: null }
      setDatasetItems(result?.items || []);
      if (result?.error) {
        toast({
          title: "Warning",
          description: result.error,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Failed to load dataset",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingDataset(false);
    }
  };

  const handleRunClick = (runId: string) => {
    if (onRunSelect) {
      onRunSelect(runId);
      // Dataset will be loaded automatically via useEffect
    }
  };

  const handleBackToRuns = () => {
    if (onRunSelect) {
      onRunSelect(null);
    }
    setDatasetItems([]);
  };

  const downloadAsJSON = () => {
    if (datasetItems.length === 0) {
      toast({
        title: "No data to export",
        description: "The dataset is empty",
        variant: "destructive",
      });
      return;
    }

    const dataStr = JSON.stringify(datasetItems, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dataset-${selectedRunId?.slice(0, 12) || "export"}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export started",
      description: "Dataset exported as JSON",
    });
  };

  const downloadAsCSV = () => {
    if (datasetItems.length === 0) {
      toast({
        title: "No data to export",
        description: "The dataset is empty",
        variant: "destructive",
      });
      return;
    }

    // Flatten nested objects for CSV
    const flattenObject = (obj: any, prefix = ""): Record<string, string> => {
      const flattened: Record<string, string> = {};
      for (const key in obj) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (value === null || value === undefined) {
          flattened[newKey] = "";
        } else if (typeof value === "object" && !Array.isArray(value)) {
          Object.assign(flattened, flattenObject(value, newKey));
        } else if (Array.isArray(value)) {
          flattened[newKey] = JSON.stringify(value);
        } else {
          flattened[newKey] = String(value);
        }
      }
      return flattened;
    };

    // Get all unique keys from all items
    const allKeys = new Set<string>();
    datasetItems.forEach((item) => {
      const flattened = flattenObject(item);
      Object.keys(flattened).forEach((key) => allKeys.add(key));
    });

    const headers = Array.from(allKeys).sort();

    // Create CSV rows
    const csvRows = [
      headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","),
    ];

    datasetItems.forEach((item) => {
      const flattened = flattenObject(item);
      const row = headers.map((header) => {
        const value = flattened[header] || "";
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const dataBlob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dataset-${selectedRunId?.slice(0, 12) || "export"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export started",
      description: "Dataset exported as CSV",
    });
  };

  if (!actorId) {
    return (
      <Tabs defaultValue="logs" className="flex-1 flex flex-col min-h-0">
        <div className="px-6 pt-4 border-b border-border">
          <TabsList className="bg-transparent h-9 p-0 space-x-6">
            <TabsTrigger
              value="logs"
              className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
            >
              Live Logs
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="logs" className="flex-1 p-4 min-h-0 mt-0">
          <LogViewer logs={logs} />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <Tabs defaultValue="logs" className="flex-1 flex flex-col min-h-0">
      <div className="px-6 pt-4 border-b border-border shrink-0">
        <TabsList className="bg-transparent h-9 p-0 space-x-6">
          <TabsTrigger
            value="logs"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Live Logs
          </TabsTrigger>
          <TabsTrigger
            value="config"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Configuration
          </TabsTrigger>
          <TabsTrigger
            value="telegram"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Runs & Datasets
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent
        value="logs"
        className="flex-1 p-4 min-h-0 mt-0 flex flex-col"
      >
        {selectedRunId && (
          <div className="mb-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-md text-xs">
            <span className="text-muted-foreground">
              Viewing logs for run:{" "}
            </span>
            <code className="font-mono text-primary">
              {selectedRunId.slice(0, 12)}...
            </code>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <LogViewer logs={selectedRunId && runLogs ? runLogs : []} />
        </div>
      </TabsContent>

      <TabsContent
        value="config"
        className="flex-1 p-8 min-h-0 mt-0 overflow-y-auto"
      >
        {isLoadingSettings ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-w-2xl mx-auto overflow-hidden">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold mb-1">
                  Actor Input Configuration
                </h3>
                <p className="text-xs text-muted-foreground">
                  Configure the input parameters for this Apify actor
                </p>
                {selectedSource.config?.actorName && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Actor:{" "}
                    <span className="font-mono">
                      {selectedSource.config.actorName}
                    </span>
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshSchema}
                disabled={isRefreshingSchema}
                className="h-8"
              >
                {isRefreshingSchema ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh Schema
              </Button>
            </div>
            <ActorConfigForm
              schema={inputSchema}
              initialValues={selectedSource.config?.input || {}}
              onSave={handleSaveConfiguration}
              isSaving={isSaving}
            />

            <Separator className="my-8" />

            {/* Filter Configuration */}
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold mb-1">
                  Job Filter Configuration
                </h3>
                <p className="text-xs text-muted-foreground">
                  Configure filtering thresholds for jobs from this source. Only
                  jobs that pass all filters will be stored.
                </p>
              </div>

              <div className="space-y-6">
                {/* Compensation Filters */}
                <div className="space-y-4">
                  <h4 className="text-xs font-medium text-foreground">
                    Compensation Thresholds
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="min_ote">Minimum OTE (USD)</Label>
                      <Input
                        id="min_ote"
                        type="number"
                        value={minOte}
                        onChange={(e) =>
                          setMinOte(parseInt(e.target.value) || 60000)
                        }
                        placeholder="60000"
                        min={0}
                      />
                      <p className="text-xs text-muted-foreground">
                        Minimum On-Target Earnings. Jobs with OTE below this
                        will be filtered out. Default: $60,000
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="min_hourly">
                        Minimum Hourly Rate (USD)
                      </Label>
                      <Input
                        id="min_hourly"
                        type="number"
                        value={minHourly}
                        onChange={(e) =>
                          setMinHourly(parseInt(e.target.value) || 15)
                        }
                        placeholder="15"
                        min={0}
                      />
                      <p className="text-xs text-muted-foreground">
                        Minimum hourly rate. Jobs with hourly rates below this
                        will be filtered out. Default: $15/hr
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Boolean Filters */}
                <div className="space-y-4">
                  <h4 className="text-xs font-medium text-foreground">
                    Job Requirements
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="require_remote"
                          className="text-sm font-normal"
                        >
                          Require Fully Remote
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Only accept jobs that are fully remote (not hybrid)
                        </p>
                      </div>
                      <Switch
                        id="require_remote"
                        checked={requireRemote}
                        onCheckedChange={setRequireRemote}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="require_contract"
                          className="text-sm font-normal"
                        >
                          Require Contract/1099
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Only accept contract or 1099 positions (not W-2
                          employees)
                        </p>
                      </div>
                      <Switch
                        id="require_contract"
                        checked={requireContract}
                        onCheckedChange={setRequireContract}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="require_target_title"
                          className="text-sm font-normal"
                        >
                          Require Target Title
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Only accept SDR, BDR, Appointment Setter, Lead Gen,
                          etc.
                        </p>
                      </div>
                      <Switch
                        id="require_target_title"
                        checked={requireTargetTitle}
                        onCheckedChange={setRequireTargetTitle}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Exclusion Filters */}
                <div className="space-y-4">
                  <h4 className="text-xs font-medium text-foreground">
                    Exclusions
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="exclude_commission_only"
                          className="text-sm font-normal"
                        >
                          Exclude Commission-Only
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Filter out jobs that are commission-only (no base
                          salary)
                        </p>
                      </div>
                      <Switch
                        id="exclude_commission_only"
                        checked={excludeCommissionOnly}
                        onCheckedChange={setExcludeCommissionOnly}
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5 flex-1">
                          <Label
                            htmlFor="exclude_title_list"
                            className="text-sm font-normal"
                          >
                            Exclude Title List
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Filter out jobs with specific title keywords. Add
                            keywords below.
                          </p>
                        </div>
                        <Switch
                          id="exclude_title_list"
                          checked={excludeTitleList}
                          onCheckedChange={setExcludeTitleList}
                        />
                      </div>

                      {excludeTitleList && (
                        <Collapsible
                          open={isTitlesListOpen}
                          onOpenChange={setIsTitlesListOpen}
                        >
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full justify-between text-xs"
                            >
                              <span>
                                {excludedTitles.length} excluded title
                                {excludedTitles.length !== 1 ? "s" : ""}
                              </span>
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${
                                  isTitlesListOpen ? "transform rotate-180" : ""
                                }`}
                              />
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 space-y-3">
                            <div className="border border-border rounded-md p-3 bg-secondary/30">
                              <div className="max-h-48 overflow-y-auto space-y-2 mb-3">
                                {excludedTitles.length === 0 ? (
                                  <p className="text-xs text-muted-foreground text-center py-4">
                                    No excluded titles configured. Add title
                                    keywords below to filter out jobs containing
                                    those terms.
                                  </p>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {excludedTitles.map((title, index) => (
                                      <div
                                        key={index}
                                        className="flex items-center gap-1 px-2 py-1 bg-background border border-border rounded-md text-xs"
                                      >
                                        <span>{title}</span>
                                        <button
                                          onClick={() =>
                                            handleRemoveTitle(title)
                                          }
                                          className="ml-1 hover:text-destructive transition-colors"
                                          type="button"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="flex gap-2">
                                <Input
                                  placeholder="Add title keyword..."
                                  value={newTitleInput}
                                  onChange={(e) =>
                                    setNewTitleInput(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddTitle();
                                    }
                                  }}
                                  className="text-xs h-8"
                                />
                                <Button
                                  size="sm"
                                  onClick={handleAddTitle}
                                  disabled={!newTitleInput.trim()}
                                  className="h-8 px-3"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="exclude_hybrid_remote"
                          className="text-sm font-normal"
                        >
                          Exclude Hybrid Remote
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Filter out jobs that require some office presence
                        </p>
                      </div>
                      <Switch
                        id="exclude_hybrid_remote"
                        checked={excludeHybridRemote}
                        onCheckedChange={setExcludeHybridRemote}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5 flex-1">
                        <Label
                          htmlFor="exclude_full_time"
                          className="text-sm font-normal"
                        >
                          Exclude Full-Time W-2
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Filter out full-time employee positions (W-2)
                        </p>
                      </div>
                      <Switch
                        id="exclude_full_time"
                        checked={excludeFullTime}
                        onCheckedChange={setExcludeFullTime}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="pt-2">
                  <Button
                    onClick={handleSaveFilterConfig}
                    disabled={isSavingFilters}
                    className="w-full sm:w-auto"
                  >
                    {isSavingFilters ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Filter Configuration"
                    )}
                  </Button>
                </div>

                <div className="pt-4 border-t border-border pb-4">
                  <p className="text-xs text-muted-foreground mb-2">
                    <strong>Note:</strong> Jobs must pass all enabled filters to
                    be stored. Disable filters you don't want to apply.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent
        value="telegram"
        className="flex-1 p-8 min-h-0 mt-0 overflow-y-auto"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-1">
              Telegram Notifications
            </h3>
            <p className="text-xs text-muted-foreground">
              Configure a Telegram channel to receive filtered jobs after
              successful runs. Only jobs that pass all filters will be sent.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="telegram_channel">Telegram Channel</Label>
              <Input
                id="telegram_channel"
                placeholder="@channel or -1001234567890"
                value={telegramChannelId}
                onChange={(e) => setTelegramChannelId(e.target.value)}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">
                Enter the channel ID (e.g., -1001234567890). Leave empty to
                disable notifications.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telegram_template">Message Template</Label>
              <Textarea
                id="telegram_template"
                placeholder="Enter message template..."
                value={telegramMessageTemplate}
                onChange={(e) => setTelegramMessageTemplate(e.target.value)}
                className="font-mono text-xs min-h-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                Supports Markdown formatting. Use{" "}
                <code className="bg-secondary px-1 rounded">*bold*</code> for
                bold text.
              </p>

              <Collapsible
                open={isTemplateVarsOpen}
                onOpenChange={setIsTemplateVarsOpen}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between h-auto p-2 hover:bg-secondary/50 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Available Template Variables
                      </span>
                    </div>
                    <ChevronDown
                      className={cn(
                        "w-3.5 h-3.5 text-muted-foreground transition-transform",
                        isTemplateVarsOpen && "transform rotate-180"
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="border border-border rounded-md bg-card p-3 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Use these variables to customize your message format:
                    </p>
                    <div className="bg-secondary/30 border border-border rounded-md p-3 text-xs font-mono space-y-1.5">
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{title}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job title
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{company}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Company name
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{type}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job type (Hourly/OTE)
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{rate}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Compensation rate
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{location}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job location
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{url}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job URL (if available)
                        </span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">
                          Conditional:
                        </strong>{" "}
                        Use{" "}
                        <code className="bg-secondary px-1 rounded">
                          {"{{#if url}}...{{/if}}"}
                        </code>{" "}
                        to conditionally include content when a URL is
                        available.
                      </p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <Button
              onClick={handleSaveTelegramChannel}
              disabled={isSavingTelegram}
              className="w-full sm:w-auto"
            >
              {isSavingTelegram ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Telegram Configuration"
              )}
            </Button>
          </div>
        </div>
      </TabsContent>

      <TabsContent
        value="runs"
        className="flex-1 flex flex-col min-h-0 mt-0 overflow-hidden"
      >
        {selectedRunId ? (
          // Run Detail View - shows logs and dataset
          <div className="flex flex-col h-full overflow-hidden">
            {/* Header with back button */}
            <div className="px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToRuns}
                  className="h-8"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Runs
                </Button>
                <div>
                  <h3 className="text-sm font-semibold">Run Details</h3>
                  <p className="text-xs text-muted-foreground font-mono">
                    Run ID: {selectedRunId}
                  </p>
                </div>
              </div>
            </div>

            {/* Logs Section */}
            <div className="flex-1 flex flex-col min-h-0 px-6 py-4 border-b border-border">
              <div className="mb-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Logs
                </h4>
              </div>
              <div className="flex-1 min-h-0">
                <LogViewer logs={runLogs || []} />
              </div>
            </div>

            {/* Dataset Section */}
            <div className="flex-1 flex flex-col min-h-0 px-6 py-4 overflow-hidden">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Dataset
                </h4>
                <div className="flex items-center gap-2">
                  {!isLoadingDataset && datasetItems.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadAsJSON}
                        className="h-7 text-xs gap-1.5"
                      >
                        <Download className="w-3.5 h-3.5" />
                        JSON
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadAsCSV}
                        className="h-7 text-xs gap-1.5"
                      >
                        <Download className="w-3.5 h-3.5" />
                        CSV
                      </Button>
                    </>
                  )}
                  {isLoadingDataset && (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {isLoadingDataset ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : datasetItems.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border border-border rounded-md">
                    <p className="text-sm">No items in this dataset</p>
                  </div>
                ) : (
                  <div className="border border-border rounded-md overflow-hidden">
                    <div className="bg-secondary/50 px-4 py-2 text-xs font-medium">
                      {datasetItems.length} items
                    </div>
                    <div className="max-h-full overflow-y-auto p-4 bg-black/5 dark:bg-black/20">
                      <pre className="text-xs font-mono">
                        {JSON.stringify(datasetItems.slice(0, 100), null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Runs List View
          <div className="flex-1 overflow-y-auto p-8">
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-1">Recent Runs</h3>
              <p className="text-xs text-muted-foreground">
                Click on a run to view its logs and dataset
              </p>
            </div>

            {!databaseRuns ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : databaseRuns.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border border-border rounded-md">
                <p className="text-sm">No runs found for this source</p>
              </div>
            ) : (
              <ActorRunsTable
                runs={databaseRuns.map((run) => ({
                  id: run.runId,
                  status: run.status,
                  startedAt: new Date(run.startedAt).toISOString(),
                  finishedAt: run.completedAt
                    ? new Date(run.completedAt).toISOString()
                    : undefined,
                  defaultDatasetId: undefined, // We'll use runId instead
                }))}
                selectedRunId={selectedRunId}
                onRunClick={handleRunClick}
              />
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
